import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'src/store';
import { selectJoinedAccounts } from '@skynet/data-shared-accounts';
import {
  selectBalanceHistory,
  selectComparisonEndDate,
  selectComparisonEndTime,
  selectComparisonStartDate,
  selectInitialBalances,
} from 'src/store/shared/balance-history/selectors';
import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import {
  getExtendedBalancesHistoryBase,
  withHistoryComparisons,
} from 'src/store/utils/balances/transformations/history';
import type { ExtendedBalancesHistoryBaseParams } from 'src/store/utils/balances/transformations/history';

/** The compare-history inputs that come from the domain (Loans / Retainers) state. */
type DomainHistoryParam =
  | 'isInitialBalanceUsed'
  | 'balances'
  | 'coldWallets'
  | 'coldWalletsSnapshots'
  | 'transactions'
  | 'supportedTokens'
  | 'tokenMap'
  | 'showCurrentAccounts'
  | 'prices'
  | 'ignoreTokens'
  | 'isIgnoreOtherTokens';

/**
 * Domain selectors for every domain-specific compare-history input, keyed by the
 * `getExtendedBalancesHistory` parameter each one feeds. All keys are required, so a domain can't
 * leave an input out.
 */
export type CompareHistoryDomainSelectors = {
  [K in DomainHistoryParam]-?: (state: RootState) => ExtendedBalancesHistoryBaseParams[K];
};

/**
 * Creates the full (unfiltered) extended-history selectors of a balances domain: one for the compare
 * table, one for the compare list.
 *
 * Both views read the exact same inputs and share one {@link getExtendedBalancesHistoryBase}; they
 * differ only in how transactions are applied. Every domain wires its inputs through here, so an
 * input can't be missing from one view or one domain only — hand-copied selectors once let the
 * Loans compare list miss the picked end snapshot time and both lists miss the price series
 * (SKY-4640).
 *
 * @param domain - The domain-specific input selectors.
 */
export const createCompareHistorySelectors = (domain: CompareHistoryDomainSelectors) => {
  const selectBase = createSelector(
    [
      domain.isInitialBalanceUsed,
      selectComparisonStartDate,
      selectComparisonEndDate,
      domain.balances,
      selectBalanceHistory,
      domain.coldWallets,
      domain.coldWalletsSnapshots,
      selectExchanges,
      selectJoinedAccounts,
      domain.transactions,
      domain.supportedTokens,
      domain.tokenMap,
      domain.showCurrentAccounts,
      domain.prices,
      selectInitialBalances,
      selectComparisonEndTime,
      domain.ignoreTokens,
      domain.isIgnoreOtherTokens,
    ],
    (
      isInitialBalanceUsed,
      comparisonStartDate,
      comparisonEndDate,
      balances,
      history,
      coldWallets,
      coldWalletsSnapshots,
      exchanges,
      accounts,
      transactions,
      supportedTokens,
      tokenMap,
      showCurrentAccounts,
      prices,
      initialValues,
      endDateSnapshot,
      ignoreTokens,
      isIgnoreOtherTokens,
    ) =>
      getExtendedBalancesHistoryBase({
        isInitialBalanceUsed,
        comparisonStartDate,
        comparisonEndDate,
        balances,
        history,
        coldWallets,
        coldWalletsSnapshots,
        exchanges,
        accounts,
        transactions,
        supportedTokens,
        tokenMap,
        showCurrentAccounts,
        prices,
        initialValues,
        endDateSnapshot,
        ignoreTokens,
        isIgnoreOtherTokens,
      }),
  );

  return {
    /** Compare table: transactions are added to the start balance. */
    selectHistory: createSelector(selectBase, (base) => withHistoryComparisons(base, false)),
    /** Compare list: the start balance is kept raw and transactions are subtracted from the net change. */
    selectHistoryUseTransactionForDiff: createSelector(selectBase, (base) =>
      withHistoryComparisons(base, true),
    ),
  };
};
